import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n';
import { Icon } from '../shared/Icon';
import {
  guestTagChipStyle,
  humanizeGuestTag,
  formatGuestTagsTip,
} from '../../lib/guestTags';
import { sourceMeta } from '../../data/reservationSources';
import type {
  ReservationSourceId,
  ServiceTimelineReservation,
} from '../../types';
import { PRESSURE_PCT, type HorizonDay } from './horizonCalendar';
import {
  dayAndMonth,
  formatClockInZone,
  formatHorizonNumber,
  metricAvailable,
  weekdayLongOf,
  weekdayOf,
} from './horizonFormat';
import type { HorizonBookingDay } from './horizonTypes';

/** Everything a hover should tell you that the row itself has no room for. */
function reservationTip(
  reservation: ServiceTimelineReservation,
  timeZone: string | null,
): string {
  const bits: string[] = [
    i18n.t('horizon:week.guestsAt', {
      count: reservation.peopleCount,
      time: formatClockInZone(reservation.startMs, timeZone),
    }),
  ];
  if (reservation.tableNames?.length) {
    bits.push(i18n.t('horizon:week.table', { tables: reservation.tableNames.join(', ') }));
  }
  // Canonical source ids, so the label matches every other tab rather than a
  // vocabulary invented here.
  if (reservation.channel) {
    bits.push(
      i18n.t('horizon:week.bookedVia', {
        source: sourceMeta(reservation.channel as ReservationSourceId).label,
      }),
    );
  }
  const campaign = reservation.utmCampaign || reservation.campaign;
  if (campaign) bits.push(i18n.t('horizon:week.campaign', { campaign }));
  if (reservation.isVip) bits.push(i18n.t('horizon:week.markedVip'));
  const tags = formatGuestTagsTip(reservation.guestTags);
  if (tags) bits.push(tags);
  if (reservation.guestNote) {
    bits.push(i18n.t('horizon:week.guestNote', { note: reservation.guestNote }));
  }
  if (reservation.staffNote) {
    bits.push(i18n.t('horizon:week.staffNote', { note: reservation.staffNote }));
  }
  return bits.join('\n');
}

function ReservationRow({
  reservation,
  timeZone,
}: {
  reservation: ServiceTimelineReservation;
  timeZone: string | null;
}) {
  const { t } = useTranslation('horizon');
  const tags = (reservation.guestTags ?? []).slice(0, 2);
  const extra = (reservation.guestTags?.length ?? 0) - tags.length;

  return (
    <li className="hz-rsv" title={reservationTip(reservation, timeZone)}>
      <span className="hz-rsv-time">
        {formatClockInZone(reservation.startMs, timeZone)}
      </span>
      <span className="hz-rsv-party">
        <Icon name="users" size={11} />
        {reservation.peopleCount}
      </span>
      {reservation.isVip && (
        <span className="hz-rsv-vip" aria-label={t('week.markedVip')}>
          <Icon name="star" size={11} fill />
        </span>
      )}
      {tags.length > 0 && (
        <span className="hz-rsv-tags">
          {tags.map((tag, i) => {
            const { name, emoji } = humanizeGuestTag(tag);
            return (
              <span
                key={`${name}-${i}`}
                className="guest-tag-chip hz-rsv-tag"
                style={guestTagChipStyle(tag.color)}
              >
                {emoji ? <em aria-hidden>{emoji}</em> : null}
                {name}
              </span>
            );
          })}
          {extra > 0 && <span className="hz-rsv-tag-more">+{extra}</span>}
        </span>
      )}
      {(reservation.guestNote || reservation.staffNote) && (
        <span className="hz-rsv-note" aria-label={t('week.hasNote')}>
          <Icon name="pencil" size={11} />
        </span>
      )}
    </li>
  );
}

/**
 * How this date's book compares with what comparable dates had booked at the
 * same lead time: the weekday-aligned date last year, or the last four same
 * weekdays when there is no last year.
 */
function PaceLine({ day }: { day: HorizonDay }) {
  const { t } = useTranslation('horizon');
  const pace = day.bookPace;
  const weekday = weekdayLongOf(day.serviceDate);

  if (!pace || pace.vsPct == null) {
    if (day.typicalCovers == null) return null;
    return (
      <span className="hz-pace-line">
        {t('week.typicalServes', { weekday, covers: formatHorizonNumber(day.typicalCovers) })}
      </span>
    );
  }

  const pct = pace.vsPct;
  const tone = pct >= 5 ? 'is-ahead' : pct <= -5 ? 'is-behind' : 'is-level';
  const basis = t(`week.basis.${pace.basis}`);
  const verdict =
    pct >= 5
      ? t('week.paceAhead', { pct, basis })
      : pct <= -5
        ? t('week.paceBehind', { pct: Math.abs(pct), basis })
        : t('week.paceLevel', { basis });

  return (
    <span
      className={`hz-pace-line ${tone}`}
      title={t('week.paceTitle', {
        basis,
        then: formatHorizonNumber(Math.round(pace.reference)),
        now: formatHorizonNumber(Math.round(pace.booked)),
      })}
    >
      {verdict}
      <em>{t('week.paceThen', { covers: formatHorizonNumber(Math.round(pace.reference)) })}</em>
    </span>
  );
}

/** Fleet view: which outlets carry this day, biggest book first. */
function OutletBreakdown({
  day,
  onFocusOutlet,
}: {
  day: HorizonDay;
  onFocusOutlet?: (outletId: string) => void;
}) {
  const { t } = useTranslation('horizon');
  const rows = day.cells
    .filter(c => c.kind === 'project' || c.kind === 'pace_only')
    .map(cell => ({
      id: cell.outletId,
      name: cell.outletName,
      covers: metricAvailable(cell.onBooks) ? (cell.onBooks.value as number) : 0,
      occupancy: cell.occupancyPct,
    }))
    .sort((a, b) => b.covers - a.covers)
    .slice(0, 6);

  if (rows.length === 0) {
    return <p className="hz-col-empty">{t('week.noOutletOpen')}</p>;
  }

  return (
    <ul className="hz-col-outlets">
      {rows.map(row => (
        <li key={row.id}>
          <button
            type="button"
            className="hz-col-outlet"
            onClick={() => onFocusOutlet?.(row.id)}
            title={t('week.openOutletBookings', { name: row.name })}
          >
            <span className="hz-col-outlet-name">{row.name}</span>
            <span className="hz-col-outlet-val">
              {formatHorizonNumber(row.covers)}
              {row.occupancy != null && (
                <em> · {Math.round(row.occupancy)}%</em>
              )}
            </span>
          </button>
        </li>
      ))}
    </ul>
  );
}

function DayColumn({
  day,
  booking,
  timeZone,
  focused,
  selected,
  flagged,
  loadingBookings,
  onSelect,
  onFocusOutlet,
}: {
  day: HorizonDay;
  booking: HorizonBookingDay | null;
  timeZone: string | null;
  focused: boolean;
  selected: boolean;
  flagged: boolean;
  loadingBookings: boolean;
  onSelect: (day: HorizonDay) => void;
  onFocusOutlet?: (outletId: string) => void;
}) {
  const { t } = useTranslation('horizon');
  const occupancy = day.occupancyPct;
  const pressure = occupancy != null && occupancy >= PRESSURE_PCT;
  // Any real book keeps a visible sliver, so 3% never reads as an empty day.
  const fill =
    day.onBooks && day.onBooks > 0
      ? Math.max(0.025, Math.min(1, (occupancy ?? 0) / 100))
      : 0;

  const classes = [
    'hz-col',
    `is-${day.state}`,
    selected ? 'is-on' : '',
    flagged ? 'is-flagged' : '',
    pressure ? 'is-pressure' : '',
    day.daysAhead === 0 ? 'is-today' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section className={classes} aria-label={`${weekdayOf(day.serviceDate)} ${dayAndMonth(day.serviceDate)}`}>
      <button
        type="button"
        className="hz-col-head"
        aria-pressed={selected}
        onClick={() => onSelect(day)}
      >
        <span className="hz-col-when">
          <span className="hz-col-dow">
            {day.daysAhead === 0 ? t('today') : weekdayOf(day.serviceDate)}
          </span>
          <span className="hz-col-date">{dayAndMonth(day.serviceDate)}</span>
          {flagged && <span className="hz-col-flag" aria-label={t('needsAttention')} />}
        </span>

        {day.state === 'open' ? (
          <>
            <span className="hz-day-track">
              <span
                className="hz-day-fill"
                style={{ '--hz-fill': fill } as CSSProperties}
              />
            </span>
            <span className="hz-col-fig">
              <strong>{formatHorizonNumber(day.onBooks ?? 0)}</strong>
              {day.capacity != null && day.capacity > 0
                ? ` ${t('week.ofCovers', { capacity: formatHorizonNumber(day.capacity) })}`
                : ` ${t('week.covers')}`}
              {occupancy != null && <em>{t('week.pctFull', { pct: Math.round(occupancy) })}</em>}
            </span>
            <PaceLine day={day} />
          </>
        ) : (
          <span className="hz-col-note">
            {day.state === 'closed' ? t('closed') : t('notBookableLabel')}
          </span>
        )}
      </button>

      {day.state === 'open' && (
        <div className="hz-col-body">
          {focused ? (
            loadingBookings && !booking ? (
              <p className="hz-col-empty">{t('week.readingBookings')}</p>
            ) : booking?.unavailableReason ? (
              <p className="hz-col-empty">{t('week.unreadable')}</p>
            ) : booking && booking.reservations.length > 0 ? (
              <>
                <ul className="hz-rsvs">
                  {booking.reservations.map(reservation => (
                    <ReservationRow
                      key={reservation.id}
                      reservation={reservation}
                      timeZone={timeZone}
                    />
                  ))}
                </ul>
                {booking.truncated && (
                  <p className="hz-col-more">
                    {t('week.truncated', { count: booking.reservations.length })}
                  </p>
                )}
              </>
            ) : (
              <p className="hz-col-empty">{t('week.noBookings')}</p>
            )
          ) : (
            <OutletBreakdown day={day} onFocusOutlet={onFocusOutlet} />
          )}
        </div>
      )}
    </section>
  );
}

/**
 * The near window as a week agenda: one column per day. With an outlet focused
 * each column lists that outlet's bookings and guest tags; across a fleet it
 * lists the outlets carrying the day so a manager can drill in.
 */
export function HorizonWeek({
  days,
  bookingDays,
  timeZone,
  focusedOutletId,
  selectedDate,
  flaggedDates,
  loadingBookings,
  onSelect,
  onFocusOutlet,
}: {
  days: HorizonDay[];
  bookingDays: HorizonBookingDay[] | null;
  timeZone: string | null;
  focusedOutletId: string | null;
  selectedDate: string | null;
  flaggedDates: Set<string>;
  loadingBookings: boolean;
  onSelect: (day: HorizonDay) => void;
  onFocusOutlet?: (outletId: string) => void;
}) {
  const { t } = useTranslation('horizon');
  const bookingByDate = new Map<string, HorizonBookingDay>();
  for (const day of bookingDays ?? []) bookingByDate.set(day.serviceDate, day);

  if (days.length === 0) {
    return (
      <p className="hz-col-empty">{t('week.nothingReadable')}</p>
    );
  }

  return (
    <div className="hz-week-grid" role="list">
      {days.map(day => (
        <DayColumn
          key={day.serviceDate}
          day={day}
          booking={bookingByDate.get(day.serviceDate) ?? null}
          timeZone={timeZone}
          focused={!!focusedOutletId}
          selected={selectedDate === day.serviceDate}
          flagged={flaggedDates.has(day.serviceDate)}
          loadingBookings={loadingBookings}
          onSelect={onSelect}
          onFocusOutlet={onFocusOutlet}
        />
      ))}
    </div>
  );
}
